import React, { useState } from "react";
import { X } from "lucide-react";

/**
 * Chip-style tag input. Adds a tag on Enter, comma, or semicolon. Backspace on
 * an empty input removes the last chip. Pasting a comma/semicolon-separated
 * string splits into multiple tags.
 */
export default function TagsInput({ value = [], onChange, placeholder, dir = "ltr", testId }) {
    const [draft, setDraft] = useState("");

    const commit = (raw) => {
        const tokens = (raw || "")
            .split(/[,;]+/)
            .map((t) => t.trim())
            .filter(Boolean);
        if (!tokens.length) return;
        const existing = new Set(value.map((v) => v.toLowerCase()));
        const next = [...value];
        for (const t of tokens) {
            if (!existing.has(t.toLowerCase())) {
                next.push(t);
                existing.add(t.toLowerCase());
            }
        }
        onChange(next);
        setDraft("");
    };

    const removeAt = (i) => {
        const next = [...value];
        next.splice(i, 1);
        onChange(next);
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" || e.key === "," || e.key === ";") {
            e.preventDefault();
            commit(draft);
        } else if (e.key === "Backspace" && !draft && value.length) {
            removeAt(value.length - 1);
        }
    };

    return (
        <div
            className="min-h-[44px] w-full rounded-xl border border-border bg-card px-2 py-1.5 flex flex-wrap gap-1.5 items-center focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary"
            dir={dir}
            data-testid={testId}
        >
            {value.map((t, i) => (
                <span
                    key={`${t}-${i}`}
                    className="inline-flex items-center gap-1 rounded-full bg-secondary text-secondary-foreground text-xs px-2.5 py-1"
                    data-testid={`${testId}-chip-${i}`}
                >
                    {t}
                    <button
                        type="button"
                        onClick={() => removeAt(i)}
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Remove tag"
                    >
                        <X className="h-3 w-3" />
                    </button>
                </span>
            ))}
            <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                onBlur={() => draft && commit(draft)}
                placeholder={value.length ? "" : placeholder}
                className="flex-1 min-w-[120px] bg-transparent outline-none text-sm py-1"
                data-testid={`${testId}-input`}
            />
        </div>
    );
}
